<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
    max-width="900"
    @input="$emit('input', $event)"
  >
    <v-card rounded="0" class="pb-edit-card">
      <v-toolbar color="kenyaGreen" dark flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.processingBatchBulkImport.editRecordTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pb-edit-body px-6 py-5">
        <v-row dense>

          <!-- Purchase Order Codes (multi-select) -->
          <v-col cols="12">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.purchaseOrdersCol`) }}</div>
            <v-autocomplete
              v-model="form.orderIds"
              :items="purchaseOrderItems"
              :loading="poLoading"
              :search-input.sync="poSearch"
              item-text="label"
              item-value="id"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              hide-details="auto"
              :error="hasFieldError('orderIds') || hasFieldError('purchaseDate')"
              :messages="fieldMessages('orderIds').concat(fieldMessages('purchaseDate'))"
              :no-data-text="poLoading ? $t('loading') : $t('noDataAvailable')"
            >
              <template v-slot:selection="{ item, parent }">
                <v-chip small close @click:close="parent.selectItem(item)">
                  {{ item.label }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Process Date (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.processDateCol`) }}</div>
            <v-menu v-model="menus.processDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.processDate"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  :error="hasFieldError('processDate')"
                  :messages="fieldMessages('processDate')"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.processDate" @input="menus.processDate = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Process Type -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.processTypeCol`) }}</div>
            <v-text-field
              v-model="form.processType"
              outlined dense hide-details="auto"
              :error="hasFieldError('processType')"
              :messages="fieldMessages('processType')"
            ></v-text-field>
          </v-col>

          <!-- Grade -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.gradeCol`) }}</div>
            <v-text-field
              v-model="form.grade"
              outlined dense hide-details="auto"
            ></v-text-field>
          </v-col>

          <!-- Season -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.seasonCol`) }}</div>
            <v-text-field
              v-model="form.season"
              outlined dense hide-details="auto"
            ></v-text-field>
          </v-col>

          <!-- Parchment (Kg) -->
          <v-col cols="12" md="6">
            <div class="d-flex align-end justify-space-between mb-1">
              <span class="text-body-2 black--text">{{ $t(`${B}.parchmentCol`) }}</span>
              <span class="text-body-2 text--secondary">KG</span>
            </div>
            <v-text-field
              v-model="form.parchmentKg"
              type="number"
              outlined dense hide-details="auto"
              :error="hasFieldError('parchmentKg')"
              :messages="fieldMessages('parchmentKg')"
            ></v-text-field>
          </v-col>

          <!-- Bags -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.bagsCol`) }}</div>
            <v-text-field
              v-model="form.bags"
              type="number"
              outlined dense hide-details="auto"
            ></v-text-field>
          </v-col>

          <!-- Vehicle Plate -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.vehiclePlateCol`) }}</div>
            <v-text-field
              v-model="form.vehiclePlate"
              outlined dense hide-details="auto"
            ></v-text-field>
          </v-col>

          <!-- Harvested (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.harvestedCol`) }}</div>
            <v-menu v-model="menus.harvested" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.harvested"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  clearable
                  v-bind="attrs" v-on="on"
                  @click:clear="form.harvested = null"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.harvested" @input="menus.harvested = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Pulped (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.pulpedCol`) }}</div>
            <v-menu v-model="menus.pulped" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.pulped"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  clearable
                  v-bind="attrs" v-on="on"
                  @click:clear="form.pulped = null"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.pulped" @input="menus.pulped = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Washed (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.washedCol`) }}</div>
            <v-menu v-model="menus.washed" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.washed"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  clearable
                  v-bind="attrs" v-on="on"
                  @click:clear="form.washed = null"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.washed" @input="menus.washed = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Drying Started (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.dryingStartedCol`) }}</div>
            <v-menu v-model="menus.dryingStarted" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.dryingStarted"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  clearable
                  v-bind="attrs" v-on="on"
                  @click:clear="form.dryingStarted = null"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.dryingStarted" @input="menus.dryingStarted = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Moved to Store (date picker) -->
          <v-col cols="12" md="6">
            <div class="mb-1 text-body-2 black--text">{{ $t(`${B}.movedToStoreCol`) }}</div>
            <v-menu v-model="menus.movedToStore" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.movedToStore"
                  outlined dense readonly
                  append-icon="mdi-calendar"
                  hide-details="auto"
                  clearable
                  v-bind="attrs" v-on="on"
                  @click:clear="form.movedToStore = null"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.movedToStore" @input="menus.movedToStore = false"></v-date-picker>
            </v-menu>
          </v-col>

        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4 px-6">
        <v-btn outlined color="primary" class="mr-3" min-width="120" :disabled="loading" @click="close">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="primary" class="white--text" min-width="120" :loading="loading" @click="save">
          {{ $t('memberDataSection.processingBatchBulkImport.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  SERVER_ERROR_CODE_I18N,
  RAW_KEY_BY_FIELD,
  buildRecordUpdatePayload,
} from "@/utils/processingBatchBulkImport";
import BuyingStationService from "@/_services/BuyingStationService";

const B = "memberDataSection.processingBatchBulkImport";

// Fields whose value is a number.
const NUM_FIELDS = new Set(["parchmentKg", "bags"]);
// Fields whose value is an array.
const ARR_FIELDS = new Set(["orderIds"]);
// Date fields stored as null if cleared.
const DATE_FIELDS = new Set(["processDate", "harvested", "pulped", "washed", "dryingStarted", "movedToStore"]);

export default {
  name: "ProcessingBatchBulkImportEditDialog",
  props: {
    value:   { type: Boolean, default: false },
    record:  { type: Object,  default: null  },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      B,
      poLoading: false,
      poSearch:  "",
      purchaseOrders: [],
      menus: {
        processDate:  false,
        harvested:    false,
        pulped:       false,
        washed:       false,
        dryingStarted: false,
        movedToStore: false,
      },
      form: this.emptyForm(),
    };
  },
  computed: {
    purchaseOrderItems() {
      return this.purchaseOrders.map((o) => ({
        id:    o.id,
        label: o.orderCode || `#${o.id}`,
      }));
    },
  },
  watch: {
    record: { immediate: true, handler(rec) { this.syncForm(rec); } },
    value(open) {
      if (open) {
        this.syncForm(this.record);
        this.loadPurchaseOrders();
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        orderIds:     [],
        grade:        "",
        season:       "",
        processDate:  null,
        processType:  "",
        parchmentKg:  "",
        bags:         "",
        vehiclePlate: "",
        harvested:    null,
        pulped:       null,
        washed:       null,
        dryingStarted: null,
        movedToStore:  null,
      };
    },
    syncForm(rec) {
      const data = (rec && rec.recordedJsonData) || {};
      const form = this.emptyForm();

      form.orderIds     = Array.isArray(data.orderIds) ? [...data.orderIds] : [];
      form.grade        = data.grade        != null ? String(data.grade)        : "";
      form.season       = data.season       != null ? String(data.season)       : "";
      form.processDate  = data.processDate  || null;
      form.processType  = data.processType  != null ? String(data.processType)  : "";
      form.parchmentKg  = data.parchmentKg  != null ? String(data.parchmentKg)  : "";
      form.bags         = data.bags         != null ? String(data.bags)         : "";
      form.vehiclePlate = data.vehiclePlate != null ? String(data.vehiclePlate) : "";
      form.harvested    = data.harvested    || null;
      form.pulped       = data.pulped       || null;
      form.washed       = data.washed       || null;
      form.dryingStarted = data.dryingStarted || null;
      form.movedToStore  = data.movedToStore  || null;

      this.form = form;
    },
    async loadPurchaseOrders() {
      if (this.purchaseOrders.length) return;
      this.poLoading = true;
      try {
        const res = await BuyingStationService.getAllPurchaseOrders();
        if (res && res.data) {
          const rows = res.data.rows || res.data || [];
          this.purchaseOrders = Array.isArray(rows) ? rows : [];
        }
      } catch (_) {
        this.purchaseOrders = [];
      } finally {
        this.poLoading = false;
      }
    },
    hasFieldError(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      return Boolean(md[field] && md[field].length);
    },
    fieldMessages(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      return (md[field] || [])
        .map((code) => {
          const key = SERVER_ERROR_CODE_I18N[code];
          return key ? this.$t(`${B}.${key}`) : code;
        })
        .filter(Boolean);
    },
    close() {
      if (this.loading) return;
      this.$emit("input", false);
    },
    save() {
      const overrides = {};
      Object.keys(RAW_KEY_BY_FIELD).forEach((field) => {
        const rawKey = RAW_KEY_BY_FIELD[field];
        const v = this.form[field];
        if (ARR_FIELDS.has(field)) {
          overrides[rawKey] = Array.isArray(v) ? v : [];
        } else if (NUM_FIELDS.has(field)) {
          const n = v === "" || v == null ? null : Number(v);
          overrides[rawKey] = Number.isFinite(n) ? n : null;
        } else if (DATE_FIELDS.has(field)) {
          overrides[rawKey] = v || null;
        } else {
          const s = v == null ? "" : String(v).trim();
          overrides[rawKey] = s === "" ? null : s;
        }
      });
      const payload = buildRecordUpdatePayload(this.record, overrides);
      this.$emit("save", { recordId: this.record.id, payload });
    },
  },
};
</script>

<style lang="scss" scoped>
.pb-edit-body {
  max-height: 70vh;
}
</style>
